/**
 * Drawing-compare feature types. Imported by the compare modals, the
 * grid-diff HTTP client (`./api.ts`), and the parent `DocumentLibraryView`
 * (only to type the in-flight job snapshot).
 */

/**
 * Drawing-compare feature config. Passed to `DocumentLibraryView` via the
 * `drawingCompare` prop — when the prop is omitted the Compare button is
 * hidden and the feature is fully disabled.
 */
export interface DrawingCompareConfig {
  /** Base URL for the BFF CAD-compare endpoints (no trailing slash). */
  apiUrl: string;
  /**
   * Returns a fresh Bearer token for the BFF. Pass the app's `getMslToken`
   * (or equivalent) here — the shared lib never calls auth utils directly.
   */
  getAuthToken: () => Promise<string>;
}

/** One bounding box returned by the grid-diff service. */
export interface GridDiffDifference {
  box_id: number;
  grid: string;
  grids: string[];
  category: string;
  description: string;
  before: string;
  after: string;
  source: string;
  reviewed: boolean;
  /** [x0, y0, x1, y1] in image pixels. */
  absolute_bbox: [number, number, number, number];
  /** [x0, y0, x1, y1] in image pixels (cluster union). */
  union_cell_bbox: [number, number, number, number];
  /** [x, y, w, h] normalized to image dimensions (0..1). */
  bbox_norm: [number, number, number, number];
}

export interface GridDiffStatus {
  job_id: string;
  status: "pending" | "processing" | "done" | "error";
  progress: number;
  message: string;
  /** Data URL (jpeg/png) of the primary preview image — used for Difference Overlay. */
  grid_preview: string | null;
  /** Data URL of the secondary preview image (second PDF). */
  grid_preview_secondary: string | null;
  /** Data URL of the annotated-diff image, when available. */
  annotated_image: string | null;
  differences: GridDiffDifference[];
  other_differences: GridDiffDifference[];
  grid_info: Partial<{
    rows: number;
    cols: number;
    image_width: number;
    image_height: number;
    pipeline: string;
    box_count: number;
    residual_box_count: number;
    max_cluster_area_ratio: number;
    min_cluster_area_ratio: number;
    max_boxes: number;
    non_overlapping_boxes: boolean;
  }>;
  summary: string;
  claude_response: string;
  raw_responses: unknown[];
  token_usage: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
  };
  patch_token_usage: unknown[];
}

/** Available Claude models for the compare setup modal. */
export const GRID_DIFF_MODEL_OPTIONS: ReadonlyArray<{
  label: string;
  value: string;
}> = [
  { label: "Claude Sonnet 4.6", value: "claude-sonnet-4-6" },
  { label: "Claude Sonnet 4.5", value: "claude-sonnet-4-5-20250929" },
  { label: "Claude Opus 4.7", value: "claude-opus-4-7" },
  { label: "Claude Opus 4.6", value: "claude-opus-4-6" },
  { label: "Claude Opus 4.5", value: "claude-opus-4-5-20250929" },
  { label: "Claude Haiku 4.5", value: "claude-haiku-4-5" }
];
